import {
  health, timeAgo, timeAgoEpoch, formatStorage, storageColor, diskInfo, diskColor,
  deviceStatus, serverStatus, backupSummary,
} from "./dashboardHelpers.js";
import { DeviceCard, DeviceHead, Facts, Meter, SyncError, StatusDot } from "./DashboardParts.jsx";

// ══════════════════════════════════════════════════════
//  Cartes d'appareils — UDM, NAS (avec dernières sauvegardes), serveurs
//  Utilisées par l'onglet Supervision (et réutilisables ailleurs).
// ══════════════════════════════════════════════════════

const GLYPH = { OK: "✓", WARNING: "!", CRITICAL: "✕" };

/** Pastille ronde avec ✓ / ! / ✕ selon le résultat d'une sauvegarde. */
function JobIcon({ status }) {
  return (
    <span style={{
      width: 16, height: 16, borderRadius: "50%", background: health(status).color, color: "#fff",
      display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, flexShrink: 0,
    }}>{GLYPH[status] || "?"}</span>
  );
}

/** Compteurs succès / avertissement / échec. */
export function BackupCounters({ counts }) {
  const pill = (n, label, status) => (
    <span style={{
      fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      color: health(status).color, background: health(status).bg, opacity: n === 0 ? .55 : 1,
    }}>{n} {label}</span>
  );
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {pill(counts.OK, "succès", "OK")}
      {pill(counts.WARNING, "avert.", "WARNING")}
      {pill(counts.CRITICAL, "échec", "CRITICAL")}
    </div>
  );
}

/** "Dernière sauvegarde : 07/10/2026 03:40 · il y a 5 h" (+ dernière réussie si la plus récente a échoué). */
export function LastBackupLine({ jobs }) {
  const { latest, lastOk } = backupSummary(jobs);
  if (!latest) return null;
  const h = health(latest.status);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, marginBottom: 8,
      background: latest.status === "OK" ? "var(--navy-mid)" : h.bg, borderRadius: 8, padding: "6px 10px",
    }}>
      <JobIcon status={latest.status} />
      <span style={{ color: "var(--text-2)" }}>Dernière sauvegarde</span>
      <strong style={{ color: "var(--text-1)" }}>{latest.lastRunLabel || "—"}</strong>
      <span style={{ color: "var(--text-3)" }}>· {timeAgoEpoch(latest.lastRunEpoch)}</span>
      {latest.status !== "OK" && lastOk && (
        <span style={{ color: "var(--text-3)", width: "100%", fontSize: 11 }}>
          Dernière réussie : {lastOk.lastRunLabel} ({timeAgoEpoch(lastOk.lastRunEpoch)})
        </span>
      )}
      {latest.status !== "OK" && !lastOk && (
        <span style={{ color: h.color, width: "100%", fontSize: 11 }}>Aucune sauvegarde réussie détectée</span>
      )}
    </div>
  );
}

/** Liste des tâches de sauvegarde d'un NAS. */
export function BackupJobList({ jobs }) {
  return (
    <div>
      {jobs.map((j, i) => (
        <div key={`${j.jobName}-${i}`}
          title={[j.rawStatus && `Résultat DSM : ${j.rawStatus}`, j.nextRunLabel && `Prochaine exécution : ${j.nextRunLabel}`].filter(Boolean).join("\n")}
          style={{
            display: "grid", gridTemplateColumns: "16px 1fr auto", gap: 8, alignItems: "center",
            padding: "5px 0", fontSize: 11.5, borderTop: i ? "1px dashed var(--border)" : "none",
          }}>
          <JobIcon status={j.status} />
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text-1)" }}>
            {j.jobName}
            {j.source && <span style={{ fontSize: 10, color: "var(--text-3)", marginLeft: 6 }}>{j.source}</span>}
          </div>
          <div style={{ fontSize: 10.5, whiteSpace: "nowrap", color: j.status === "CRITICAL" ? "var(--danger)" : "var(--text-3)" }}>
            {j.lastRunLabel || "—"}
            {j.durationLabel ? ` · ${j.durationLabel}` : ""}
            {j.status !== "OK" && j.rawStatus ? ` · ${j.rawStatus}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
//  NAS
// ─────────────────────────────────────────────

export function NasCard({ nas }) {
  const status = deviceStatus(nas);
  const jobs = nas.backupJobs || [];
  const { counts } = backupSummary(jobs);
  const raidBad = nas.raidStatus && nas.raidStatus.toLowerCase() !== "normal";
  const pct = nas.storagePercent;

  return (
    <DeviceCard status={status}>
      <DeviceHead status={status} name={nas.name}
        sub={[nas.companyName, nas.model, nas.dsmVersion].filter(Boolean).join(" · ")} />
      <SyncError message={nas.lastError} />

      <Facts items={[
        ["RAID", nas.raidStatus || "—", raidBad ? "var(--danger)" : undefined],
        ["Température", nas.temperatureC != null ? `${nas.temperatureC} °C` : "—"],
      ]} />

      <Meter label={`Stockage — ${formatStorage(nas.storageUsedGb)} / ${formatStorage(nas.storageTotalGb)}`}
        value={pct} color={storageColor(pct)} />

      {(nas.disks || []).length > 0 && (
        <div style={{ display: "flex", gap: 5, marginTop: 10, alignItems: "center", fontSize: 11, color: "var(--text-3)", flexWrap: "wrap" }}>
          Baies :
          {nas.disks.map((d, i) => (
            <span key={i} title={`Baie ${d.bay}${d.model ? " — " + d.model : ""}`} style={{
              minWidth: 22, height: 14, padding: "0 3px", borderRadius: 3, fontSize: 9, fontWeight: 700,
              display: "grid", placeItems: "center", color: "#fff", background: health(d.status).color,
            }}>{d.bay}</span>
          ))}
        </div>
      )}

      {/* ── Sauvegardes ── */}
      <div style={{ marginTop: 12, paddingTop: 11, borderTop: "1px solid var(--border)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, flexWrap: "wrap", marginBottom: 8, fontSize: 11, color: "var(--text-3)" }}>
          <span>Sauvegardes ({jobs.length})</span>
          {jobs.length > 0 && <BackupCounters counts={counts} />}
        </div>
        {jobs.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
            Aucune tâche de sauvegarde détectée — Hyper Backup / Active Backup absent, ou accès non autorisé pour le compte de supervision.
          </div>
        ) : (
          <>
            <LastBackupLine jobs={jobs} />
            <BackupJobList jobs={jobs} />
          </>
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 10.5, color: "var(--text-3)" }}>
        Synchro : {timeAgo(nas.lastSyncedAt)}
      </div>
    </DeviceCard>
  );
}

// ─────────────────────────────────────────────
//  UDM / passerelles UniFi
// ─────────────────────────────────────────────

function Metric({ label, value, color }) {
  return (
    <div style={{ background: "var(--navy-mid)", borderRadius: 8, padding: "8px 10px", minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: color || "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-3)" }}>{label}</div>
    </div>
  );
}

export function GatewayCard({ gateway: g }) {
  const status = deviceStatus(g);
  const off = status === "CRITICAL" && g.wanStatus && /hors/i.test(g.wanStatus);
  return (
    <DeviceCard status={status}>
      <DeviceHead status={status} name={g.name}
        sub={[g.companyName, g.model, g.publicIp && `IP publique ${g.publicIp}`].filter(Boolean).join(" · ")} />
      <SyncError message={g.lastError} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 }}>
        <Metric label="WAN" value={g.wanStatus || "—"} color={off ? "var(--danger)" : undefined} />
        <Metric label="Latence" value={g.latencyMs != null ? `${g.latencyMs} ms` : "—"} />
        <Metric label="Clients" value={g.connectedClients ?? "—"} />
      </div>

      <Facts bordered={false} items={[
        ["↓ Descendant", g.downMbps != null ? `${g.downMbps} Mbps` : "—"],
        ["↑ Montant", g.upMbps != null ? `${g.upMbps} Mbps` : "—"],
        ["Disponibilité", g.uptimePercent != null ? `${g.uptimePercent} %` : "—"],
        ["Dernier contact", g.lastSeenAt ? timeAgo(g.lastSeenAt) : "jamais"],
        ["Fournisseur (FAI)", g.ispName ? `${g.ispName}${g.ispAsn ? ` · ${g.ispAsn}` : ""}` : "—"],
        ["Perte de paquets", g.packetLossPercent != null ? `${g.packetLossPercent} %` : "—",
          g.packetLossPercent >= 5 ? "var(--danger)" : g.packetLossPercent >= 1 ? "var(--warning)" : undefined],
        ...(g.wanDowntime > 0 ? [["Coupure (5 dernières min)", "détectée", "var(--danger)"]] : []),
      ]} />

      <div style={{ marginTop: 8, fontSize: 10.5, color: "var(--text-3)" }}>Synchro : {timeAgo(g.lastSyncedAt)}</div>
    </DeviceCard>
  );
}

// ─────────────────────────────────────────────
//  Serveurs (Atera) — même carte que l'ancienne section du tableau de bord
// ─────────────────────────────────────────────

export function ServerCard({ server: a }) {
  const online = a.status === "ONLINE";
  const disk = diskInfo(a);
  const status = serverStatus(a);
  return (
    <DeviceCard status={status} style={!online ? { background: "var(--danger-dim)" } : undefined}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <StatusDot status={online ? "OK" : "CRITICAL"} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.hostname || "—"}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.companyName || "—"}</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, color: online ? "var(--success)" : "var(--danger)", whiteSpace: "nowrap" }}>
          {online ? "EN LIGNE" : "HORS LIGNE"}
        </span>
      </div>

      <Facts items={[
        ["IP", a.ipAddress],
        ["OS", a.os],
        ["CPU", a.cpu],
        ["RAM", a.ramGb ? `${a.ramGb} Go` : "—"],
        ["Dernier reboot", a.lastRebootTime ? new Date(a.lastRebootTime).toLocaleDateString("fr-FR") : "—"],
        ["Connecté", a.currentLoggedUser],
      ]} />

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
        <div style={{ flex: 1 }}>
          {disk ? <Meter label="Disque" value={disk.pct} color={disk.color} />
                : <div style={{ fontSize: 11, color: "var(--text-3)" }}>Disque — inconnu</div>}
        </div>
        <div style={{ textAlign: "right", fontSize: 10.5, whiteSpace: "nowrap", color: online ? "var(--text-3)" : "var(--danger)", fontWeight: online ? 400 : 600 }}>
          Agent : {a.lastSeenAt ? timeAgo(a.lastSeenAt) : "jamais connecté"}
        </div>
      </div>
    </DeviceCard>
  );
}